// DemographicSelector.tsx
import { Box, Flex } from '@chakra-ui/react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ConversationType, DtType } from '../../generated/graphql';
import MultiSelectDemographicBar from './MultiSelectDemographicBar';
import MultiSelectDemographicFilter from './MultiSelectDemographicFilter';

// Define types for the component
export interface DemographicType {
  key: string;
  value: string;
}

const getDemographics = (dtType: DtType): Record<string, string[]> => {
  if (dtType === DtType.Congressperson) {
    return {
      party: ['Democrat', 'Republican', 'Independent'],
      chamber: ['House', 'Senate'],
    };
  } else if (dtType === DtType.MoviesSimple) {
    return {
      gender: ['Female', 'Male'],
    };
  } else if (dtType === DtType.MoviesFull || dtType === DtType.MoviesMedium) {
    return {
      gender: ['Female', 'Male'],
      ethnicity: [
        'American Indian or Alaska Native',
        'Asian',
        'Black or African American',
        'Hispanic',
        'Pacific Islander',
        'White',
      ],
      region: ['Midwest', 'Northeast', 'South', 'West'],
    };
  }

  return {};
};

interface DemographicSelectorProps {
  dtType: DtType;
  onSelectionChange?: (selectedDemographics: DemographicType[]) => void;
  type: ConversationType;
  maxSelections?: number;
}

const DemographicSelector: React.FC<DemographicSelectorProps> = ({
  dtType,
  onSelectionChange,
  type,
  maxSelections: propMaxSelections,
}) => {
  const prevSelectionRef = useRef<string>('');
  const [selectedDemographicIds, setSelectedDemographicIds] = useState<
    Set<string>
  >(new Set());
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(true);

  // Get all available demographics based on the dtType
  const allDemographics = useMemo(() => getDemographics(dtType), [dtType]);

  // Default max selections is 6, or 1 for Persona type conversations
  const maxSelections =
    propMaxSelections || (type === ConversationType.Persona ? 1 : 6);

  // Convert selected IDs to demographic objects
  const selectedDemographics = useMemo(() => {
    return Array.from(selectedDemographicIds).map((id) => {
      const [key, value] = id.split('-', 2);
      return { key, value };
    });
  }, [selectedDemographicIds]);

  // This effect notifies the parent component when selections change
  useEffect(() => {
    if (onSelectionChange && selectedDemographics.length > 0) {
      // Add a deeper comparison to prevent unnecessary updates
      const currentSelectionStr = JSON.stringify(selectedDemographics);
      // Use a ref to store the previous selection
      if (currentSelectionStr !== prevSelectionRef.current) {
        prevSelectionRef.current = currentSelectionStr;
        onSelectionChange([...selectedDemographics]);
      }
    }
  }, [selectedDemographics, onSelectionChange]);

  // Handle removing a demographic from the MultiSelectDemographicBar
  const handleRemoveDemographic = useCallback(
    (demographic: DemographicType) => {
      setSelectedDemographicIds((prev) => {
        // Create a new Set, update it, and return
        const updated = new Set(Array.from(prev));
        updated.delete(`${demographic.key}-${demographic.value}`);
        return updated;
      });
    },
    []
  );

  // Toggle dropdown visibility
  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen((prev) => !prev);
  }, []);

  return (
    <Box width="100%">
      <Flex direction="column" gap={0} width="full">
        {/* Horizontal scrollable bar with selected demographics */}
        <MultiSelectDemographicBar
          selectedDemographics={selectedDemographics}
          onRemoveDemographic={handleRemoveDemographic}
          onAddDemographic={toggleDropdown}
          maxSelections={maxSelections}
          type={type}
        />

        {/* Dropdown for selecting demographics - conditionally rendered */}
        {isDropdownOpen && (
          <Flex width="full" flexDirection="row" justifyContent="flex-end">
            <MultiSelectDemographicFilter
              selectedDemographicIds={selectedDemographicIds}
              setSelectedDemographicIds={setSelectedDemographicIds}
              allDemographics={allDemographics}
              maxSelections={maxSelections}
              onClose={() => setIsDropdownOpen(false)}
            />
          </Flex>
        )}
      </Flex>
    </Box>
  );
};

export default React.memo(DemographicSelector);
